// Session-scoped analytics funnel. Stitches CTA clicks → intermediate steps
// → conversions into a single attributable journey so downstream tools
// (GA4 explorations, Looker, PostHog funnels) can answer:
//
//   "Of users who clicked CTA X on page Y, how many converted via Z and how
//    long did it take?"
//
// Stored in sessionStorage so it survives client-side navigation but resets
// per tab/session. Safe to import from SSR — all access guards on `window`.

export const FUNNEL_STORAGE_KEY = "fenny.funnel.v1";
const FUNNEL_TTL_MS = 30 * 60 * 1000; // 30 min — match a typical session

export interface FunnelState {
  funnel_id: string;
  origin_cta_id?: string;
  origin_location?: string;
  origin_label?: string;
  started_at: number;
  last_step_at: number;
  step_count: number;
  steps: string[];
}

function makeId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `f_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function read(): FunnelState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(FUNNEL_STORAGE_KEY);
    if (!raw) return null;
    const state = JSON.parse(raw) as FunnelState;
    if (Date.now() - state.last_step_at > FUNNEL_TTL_MS) {
      window.sessionStorage.removeItem(FUNNEL_STORAGE_KEY);
      return null;
    }
    return state;
  } catch {
    return null;
  }
}

function write(state: FunnelState | null): void {
  if (typeof window === "undefined") return;
  try {
    if (state) {
      window.sessionStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify(state));
    } else {
      window.sessionStorage.removeItem(FUNNEL_STORAGE_KEY);
    }
  } catch {
    // sessionStorage can be disabled (private mode, quotas) — fail soft.
  }
}

/** Read-only snapshot for attaching context to events. */
export function getFunnel(): FunnelState | null {
  return read();
}

/** Start (or restart) a funnel from a CTA click. */
export function startFunnelFromCta(params: {
  cta_id: string;
  location?: string;
  label?: string;
}): FunnelState {
  const now = Date.now();
  const state: FunnelState = {
    funnel_id: makeId(),
    origin_cta_id: params.cta_id,
    origin_location: params.location,
    origin_label: params.label,
    started_at: now,
    last_step_at: now,
    step_count: 1,
    steps: [`cta:${params.cta_id}`],
  };
  write(state);
  return state;
}

/** Record an intermediate step (form view, field focus, submit attempt…). */
export function recordFunnelStep(name: string): FunnelState | null {
  const state = read();
  if (!state) return null;
  state.last_step_at = Date.now();
  state.step_count += 1;
  state.steps.push(name);
  // Cap history to keep payloads small.
  if (state.steps.length > 24) state.steps = state.steps.slice(-24);
  write(state);
  return state;
}

/** Close the funnel and return the final state for the conversion event. */
export function completeFunnel(): FunnelState | null {
  const state = read();
  if (!state) return null;
  state.last_step_at = Date.now();
  state.step_count += 1;
  state.steps.push("conversion");
  write(null);
  return state;
}

/** Flatten funnel state into analytics-friendly scalar fields. */
export function funnelDimensions(
  state: FunnelState | null,
): Record<string, string | number | undefined> {
  if (!state) return {};
  return {
    funnel_id: state.funnel_id,
    funnel_origin_cta_id: state.origin_cta_id,
    funnel_origin_location: state.origin_location,
    funnel_origin_label: state.origin_label,
    funnel_step_count: state.step_count,
    funnel_duration_ms: Date.now() - state.started_at,
    funnel_path: state.steps.join(" > "),
  };
}
